import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProductModal } from '../../context/ProductModalContext';
import { getCategories, getProducts } from '../../api/apiServices'; // ← add getProducts
import { slugify } from '../../utils/slugify';

export default function SearchOverlay({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [products, setProducts] = useState([]);              // ← real products
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const inputRef = useRef(null);
  const { openProductModal } = useProductModal();
  const navigate = useNavigate();

  // Fetch real data once when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [productsRes, categoriesRes] = await Promise.all([
          getProducts(),       // ← your real products endpoint
          getCategories(),
        ]);

        setProducts(productsRes || []);                    // adjust if wrapped in .data
        setCategories(categoriesRes.data || []);
      } catch (err) {
        console.error("Failed to load data in search overlay:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle body scroll & focus
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setQuery('');
      setResults([]);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Real-time search filtering on real products
  useEffect(() => {
    if (query.trim().length < 2) {  // optional: min 2 chars to reduce noise
      setResults([]);
      return;
    }

    const q = query.toLowerCase().trim();

    const filtered = products.filter((p) => {
      return (
        p.name?.toLowerCase().includes(q) ||
        p.category?.name?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        // optional: search in tags / other fields if they exist
        false
      );
    });

    setResults(filtered);
  }, [query, products]); // ← depend on real products

  const handleProductClick = (product) => {
    onClose();
    // Adapt product shape to whatever your modal expects
    setTimeout(() => {
      openProductModal({
        id: product._id,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice,
        image: product.images?.[0] || product.image,
        badges: product.badge ? [product.badge] : [],
        description: product.description,
        // add more fields if your modal needs them
      });
    }, 300);
  };

  const handleCategoryClick = (categoryName) => {
    const slug = slugify(categoryName);
    onClose();
    navigate(`/products/${slug}`);
  };

  const displayedCategories = categories.slice(0, 10);

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        className={`fixed inset-0 bg-black/60 z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      <div
        className={`fixed top-0 left-0 right-0 bg-white z-50 transition-transform duration-300 ease-out ${isOpen ? 'translate-y-0' : '-translate-y-full'}`}
      >
        <div className="max-w-[1200px] mx-auto px-4 py-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 relative">
              <i className="ri-search-line text-xl text-gray-400 absolute left-4 top-1/2 -translate-y-1/2"></i>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for dresses, tops, accessories..."
                className="w-full h-12 pl-12 pr-10 rounded-full bg-gray-100 text-sm font-['Poppins'] text-gray-800 placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#EF579A]/30 transition-all"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-circle-fill text-lg"></i>
                </button>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-sm font-['Poppins'] font-medium text-gray-500 hover:text-[#EF579A] transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
          </div>

          {query.trim().length === 0 ? (
            <div className="pb-4">
              <p className="text-xs font-['Poppins'] font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Popular Categories
              </p>

              {loading ? (
                <div className="text-sm text-gray-400">Loading...</div>
              ) : displayedCategories.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {displayedCategories.map((cat) => (
                    <button
                      key={cat._id}
                      onClick={() => handleCategoryClick(cat.name)}
                      className="px-4 py-2 rounded-full bg-gray-100 text-sm font-['Poppins'] text-gray-700 hover:bg-[#EF579A] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-500">No categories available</div>
              )}
            </div>
          ) : (
            <div className="pb-4 max-h-[60vh] overflow-y-auto">
              {loading ? (
                <div className="text-center py-10 text-gray-500">Loading products...</div>
              ) : results.length > 0 ? (
                <>
                  <p className="text-xs font-['Poppins'] font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    {results.length} result{results.length !== 1 ? 's' : ''} found
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {results.map((product) => {
                      const discount = product.originalPrice && product.originalPrice > product.price
                        ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
                        : 0;

                      return (
                        <button
                          key={product._id}
                          onClick={() => handleProductClick(product)}
                          className="text-left group cursor-pointer"
                        >
                          <div className="relative aspect-[4/5] bg-[#faf8f8] rounded-xl overflow-hidden mb-2">
                            <img
                              src={product.images?.[0] || '/placeholder.jpg'} // fallback if no image
                              alt={product.name}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                            />
                            {discount > 0 && (
                              <div className="absolute top-2 right-2 bg-[#EF579A] text-white rounded-full py-0.5 px-2">
                                <p className="text-[10px] font-['Poppins'] font-bold">-{discount}%</p>
                              </div>
                            )}
                          </div>
                          <p className="font-['Poppins'] text-xs font-medium text-gray-800 truncate group-hover:text-[#EF579A] transition-colors">
                            {product.name}
                          </p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="font-['Poppins'] text-xs font-bold text-[#EF579A]">
                              ₦{Number(product.price).toLocaleString()}
                            </span>
                            {product.originalPrice && product.originalPrice > product.price && (
                              <span className="font-['Poppins'] text-[10px] text-gray-400 line-through">
                                ₦{Number(product.originalPrice).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="pb-4 text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-full">
                    <i className="ri-search-line text-2xl text-gray-300"></i>
                  </div>
                  <p className="font-['Poppins'] text-sm text-gray-400">
                    No results found for "{query}"
                  </p>
                  <p className="font-['Poppins'] text-xs text-gray-300 mt-1">
                    Try a different search term
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}